import React, { Component } from 'react';

import './AdminPage.css';
import { saveGallery } from '../../actions/GalleryActions';
import FolderList from './FolderList';
import { listFolder, isFolder, isMedia, guessFolderType, summarizeFiles, HIDDEN, TYPE_IMG } from '../../lib/driveMedia';

function parseShare(url) {
  // Remove any query parameters, fragments, and extra trailing slashes
  const cleanUrl = url.split('?')[0].split('#')[0].replace(/\/+$|\/\/+/, '')

  // Split the URL by slashes and filter out empty parts
  const parts = cleanUrl.split('/').filter(part => part !== '');

  // Find the "folders" part and return the next segment as the ID
  const folderIndex = parts.indexOf('folders');

  // Return the ID if "folders" is found and has a following segment, else first part
  return parts[folderIndex + 1];
}

// Folders without media start unticked; their type defaults to Photos if the admin ticks them
function toFolderRow(id, name, files) {
  const guess = guessFolderType(files);
  return { id, name, summary: summarizeFiles(files), show: guess !== HIDDEN, type: guess === HIDDEN ? TYPE_IMG : guess };
}

export default class AdminPage extends Component {
  state = {
    loading: false,
    error: '',
    galleryId: '',
    galleryLink: '',
    copied: false,
    folders: [],
    foldersLoading: false,
    foldersError: '',
    loadedFor: '',
  };

  loadFolders = async value => {
    const folderId = parseShare((value || '').trim());
    if (!folderId || folderId === this.state.loadedFor) return;

    this.setState({ folders: [], foldersLoading: true, foldersError: '', loadedFor: folderId });

    try {
      const children = await listFolder(folderId);
      const subfolders = children.filter(isFolder);

      let folders = [];
      if (subfolders.length) {
        folders = await Promise.all(
          subfolders.map(async folder => {
            const files = await listFolder(folder.id, 'mimeType,size');
            return toFolderRow(folder.id, folder.name, files);
          })
        );
      } else if (children.some(isMedia)) {
        folders = [toFolderRow(folderId, '(this folder)', children)];
      }

      if (this.state.loadedFor !== folderId) return; // a newer link was pasted meanwhile

      this.setState({
        folders,
        foldersLoading: false,
        foldersError: folders.length ? '' : 'No subfolders or media files found.',
      });
    } catch (error) {
      if (this.state.loadedFor !== folderId) return;
      this.setState({ foldersLoading: false, foldersError: error.message, loadedFor: '' });
    }
  };

  updateFolder = (id, changes) => {
    this.setState(({ folders }) => ({ folders: folders.map(folder => (folder.id === id ? { ...folder, ...changes } : folder)) }));
  };

  setFolderType = (id, type) => this.updateFolder(id, { type });

  setFolderShow = (id, show) => this.updateFolder(id, { show });

  handleSubmit = event => {
    event.preventDefault();

    const form = event.target;
    const data = {};

    const folderId = parseShare(form.elements.shareURL.value.trim());
    if (folderId !== this.state.loadedFor || this.state.foldersLoading) {
      this.loadFolders(form.elements.shareURL.value);
      return undefined;
    }

    form.elements.shareURL.value = parseShare(form.elements.shareURL.value.trim());

    for (let element of form.elements) if (element.name) data[element.name] = element.value;

    data.sources = this.state.folders
      .filter(folder => folder.show)
      .map(({ id, name, type }) => ({ id, name, type }));

    if (!data.sources.length) return this.setState({ error: 'Pick at least one folder to show.' });

    this.setState({ loading: true, error: '', galleryId: '', copied: false });

    return saveGallery(data)
      .then(response => response.json())
      .then(response => {
        if (!response || !response.success) {
          throw new Error('Error creating gallery. ' + (response && response.error ? response.error : ''));
        }

        const PORT = window.location.port ? ':' + window.location.port : '';
        const galleryLink = `${window.location.protocol}//${window.location.hostname}${PORT}/gallery/${response.galleryId}`;
        this.setState({ loading: false, galleryId: response.galleryId, galleryLink, folders: [], loadedFor: '' });
        form.reset();
      })
      .catch(error => this.setState({ loading: false, error: error.message }));
  };

  copyLink = () => {
    const done = () => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    };

    // navigator.clipboard only exists on https / localhost
    if (navigator.clipboard) return navigator.clipboard.writeText(this.state.galleryLink).then(done);

    const textarea = document.createElement('textarea');
    textarea.value = this.state.galleryLink;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    done();
  };

  render() {
    const { loading, error, galleryId, galleryLink, copied, folders, foldersLoading, foldersError } = this.state;
    const visibleCount = folders.filter(folder => folder.show).length;

    return (
      <div className="admin-page">
        <div className="admin-card">
          <header className="admin-header">
            <span className="admin-eyebrow">Admin</span>
            <h1>Create a gallery</h1>
            <p>Point to a Google Drive folder and get a shareable gallery link.</p>
          </header>

          <form className="admin-form" onSubmit={this.handleSubmit}>
            <div className="field">
              <label htmlFor="shareURL">Google Drive folder</label>
              <div className="input-with-button">
                <input
                  type="text"
                  id="shareURL"
                  name="shareURL"
                  placeholder="Folder URL or ID"
                  required
                  onBlur={event => this.loadFolders(event.target.value)}
                  onPaste={event => {
                    const input = event.target;
                    setTimeout(() => this.loadFolders(input.value), 0);
                  }}
                />
                <button
                  type="button"
                  className="admin-secondary"
                  onClick={() => {
                    this.setState({ loadedFor: '' }, () => this.loadFolders(document.getElementById('shareURL').value));
                  }}
                >
                  Load folders
                </button>
              </div>
              <small>Paste the folder URL or its ID. The folder must be shared as “Anyone with the link”.</small>
            </div>

            <div className="field">
              <FolderList folders={folders} loading={foldersLoading} error={foldersError} onTypeChange={this.setFolderType} onShowChange={this.setFolderShow} />
            </div>

            <div className="field-row">
              <div className="field">
                <label htmlFor="logo">Logo file</label>
                <input type="text" id="logo" name="logo" defaultValue="logo.png" placeholder="logo.png" required />
              </div>
              <div className="field">
                <label htmlFor="background">Background file</label>
                <input type="text" id="background" name="background" defaultValue="background.png" placeholder="background.png" required />
              </div>
            </div>
            <small className="field-note">File names inside the folder. They are excluded from the photo list.</small>

            <div className="field-row">
              <div className="field">
                <label htmlFor="imagesPerPage">Images per page</label>
                <input type="number" id="imagesPerPage" name="imagesPerPage" defaultValue="5" min="1" required />
              </div>
              <div className="field">
                <label htmlFor="password">Passcode <span className="optional">optional</span></label>
                <input type="text" id="password" name="password" inputMode="numeric" placeholder="4–8 digits" pattern="\d{4,8}"
                       onInput={(e) => {e.target.value = e.target.value.replace(/\D/g, "");}}/>
              </div>
            </div>

            <button type="submit" className="admin-submit" disabled={loading || !visibleCount}>
              {loading ? 'Creating…' : 'Create gallery'}
            </button>
          </form>

          {error && (
            <div className="admin-alert admin-alert--error" role="alert">{error}</div>
          )}

          {galleryId && (
            <div className="admin-alert admin-alert--success" role="status">
              <div className="admin-alert-title">Gallery <strong>{galleryId}</strong> created</div>
              <div className="admin-link-row">
                <a href={galleryLink} target="_blank" rel="noopener noreferrer">{galleryLink}</a>
                <button type="button" className="admin-copy" onClick={this.copyLink}>{copied ? 'Copied' : 'Copy'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
